import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Link } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Phone as PhoneIcon, Clock } from "lucide-react";
import { Logo } from "@/components/site/Header";
import { useSettings, Phone } from "@/context/SettingsContext";

export const waLink = (p: Phone) => `https://wa.me/${(p.whatsapp || p.number).replace(/\D/g, "")}`;

export const telLink = (n: string) => `tel:+${n.replace(/\D/g, "")}`;

export const ContactList = ({ light = false }: { light?: boolean }) => {
  const { s } = useSettings();
  if (!s) return null;
  const muted = light ? "text-white/65" : "text-navy/65";
  const mails = [["Bookings", s.booking_email], ["Customer service", s.support_email], ["General", s.email]].filter(([, v], i, a) => v && a.findIndex(([, x]) => x === v) === i);
  return (
    <ul className={`space-y-3 text-sm ${muted}`} data-testid="contact-list">
      {mails.map(([l, v], i) => (
        <li key={l} className="flex items-center gap-2.5"><Mail size={15} className="text-brass" />
          <a href={`mailto:${v}`} className="hover:underline" data-testid={i === 0 ? "contact-email" : `contact-email-${i}`}>{mails.length > 1 ? `${l}: ` : ""}{v}</a></li>
      ))}
      {s.business_phone && (
        <li className="flex items-center gap-2.5"><PhoneIcon size={15} className="text-brass" />
          <a href={telLink(s.business_phone)} className="hover:underline" data-testid="contact-business-phone">Call: {s.business_phone}</a></li>
      )}
      {s.phones?.map((p, i) => (
        <li key={i} className="flex items-center gap-2.5"><MessageCircle size={15} className="text-brass" />
          <a href={waLink(p)} target="_blank" rel="noreferrer" className="hover:underline" data-testid={`contact-whatsapp-${i}`}>
            WhatsApp {p.label}: {p.number}
          </a></li>
      ))}
      {s.support_hours && (
        <li className="flex items-center gap-2.5"><Clock size={15} className="text-brass" /><span data-testid="contact-hours">Support inquiries: {s.support_hours}</span></li>
      )}
      {s.offices?.map((o, i) => (
        <li key={i} className="flex items-start gap-2.5"><MapPin size={15} className="mt-0.5 text-brass" />
          <span data-testid={`contact-office-${i}`}>{o.name}: {o.status === "coming_soon" ? "Coming soon" : o.address}</span></li>
      ))}
    </ul>
  );
};

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [ok, setOk] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/marketing/subscribe", { email, consent: ok, source: "footer" }); setDone(true); } catch (err) { toast.error(errMsg(err)); }
  };
  if (done) return <p className="text-sm text-brass" data-testid="newsletter-success">Thank you — you're on the list. You can unsubscribe anytime.</p>;
  return (
    <form onSubmit={submit} className="max-w-sm space-y-2" data-testid="newsletter-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brass">Offers & updates</p>
      <div className="flex gap-2">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="w-full rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-brass" data-testid="newsletter-email" />
        <button className="rounded-full bg-brass px-4 text-sm font-semibold text-navy" data-testid="newsletter-submit">Join</button>
      </div>
      <label className="flex items-start gap-2 text-xs text-white/55"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-0.5" data-testid="newsletter-consent" /> I agree to receive promotional emails from Trust Logistics LLC. Unsubscribe anytime.</label>
    </form>
  );
};

export const Footer = () => {
  const { s } = useSettings();
  const socials = Object.entries(s?.social || {}).filter(([, v]) => v);
  const cols = [
    { title: "Book", links: [["/stays", "Stays in Accra"], ["/vehicles", "Vehicle Collection"], ["/tours", "Tours & Experiences"], ["/airport-transfers", "Airport Pickups"], ["/my-bookings", "My Bookings"]] },
    { title: "Company", links: [["/about", "About Us"], ["/services", "Services"], ["/reviews", "Guest Reviews"], ["/coming-soon", "Coming Soon"], ["/estates", "Gant Estates — In development (2027)"], ["/shipping", "International Shipping — In development (2027)"], ["/partner", "Partner With Us"], ["/investors", "Investor Inquiries"], ["/support", "Contact"]] },
    { title: "Policies", links: [["/terms", "Terms of Service"], ["/privacy", "Privacy Policy"], ["/policies/cancellation", "Cancellation"], ["/policies/refund", "Refunds"], ["/policies", "All policies"]] },
  ];
  return (
    <footer className="relative bg-navy pb-24 text-white grain" data-testid="site-footer">
      <div className="container-x relative grid gap-12 py-16 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
        <div className="space-y-6">
          <Logo light />
          <p className="max-w-sm text-sm text-white/60">{s?.footer_text}</p>
          <ContactList light />
          <Newsletter />
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {socials.map(([k, v]) => (
                <a key={k} href={v} target="_blank" rel="noreferrer" className="rounded-full border border-white/15 px-3 py-1 text-xs capitalize text-white/70 hover:text-white" data-testid={`social-${k}`}>{k}</a>
              ))}
            </div>
          )}
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brass">{c.title}</p>
            <ul className="mt-5 space-y-3 text-sm">
              {c.links.map(([to, label]) => (
                <li key={to}><Link to={to} className="text-white/70 transition-colors hover:text-white" data-testid={`footer-link-${to.replace(/\//g, "-").slice(1)}`}>{label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="container-x relative flex flex-col justify-between gap-2 border-t border-white/10 py-6 text-xs text-white/45 sm:flex-row">
        <span>© {new Date().getFullYear()} {s?.business_name || "Trust Logistics LLC"}. All rights reserved.</span>
        <span>trustlogisticsllc.org</span>
      </div>
    </footer>
  );
};
